// PROPFIND multistatus parsing for the sync client
// (webdavsync-as-an-acequia-js-capability).
//
// Regex-based and dependency-free, in the style of browserdav/webdav/davXml.js,
// so it runs where there is no DOMParser: Node, the service worker. Elements
// are matched by LOCAL NAME across any prefix — the rule the app's DOMParser
// version applied with getElementsByTagNameNS('*', …) — so nephele's
// default-namespace <response> and BrowserDAV's <D:response> parse alike.
//
// Each member's <status> is read, at the propstat and at the response level
// (nephele answers an unauthorized child with a bare response-level 401
// inside the 207). A member that is refused is reported with its status, not
// invented as present or dropped as absent.

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }

export function unescapeXml(text) {
    if (!text || text.indexOf('&') === -1) return text
    return text.replace(/&(amp|lt|gt|quot|apos|#(\d+)|#x([0-9a-fA-F]+));/g, (m, name, dec, hex) => {
        if (dec) return String.fromCodePoint(Number(dec))
        if (hex) return String.fromCodePoint(parseInt(hex, 16))
        return ENTITIES[name] ?? m
    })
}

const NAME = '(?:[\\w.-]+:)?'

/** Every <local …>…</local> block in a fragment (nested same-name blocks are not expected in DAV bodies). */
function blocks(fragment, local) {
    const re = new RegExp(`<${NAME}${local}(?:\\s[^>]*)?>([\\s\\S]*?)<\\/${NAME}${local}\\s*>`, 'g')
    const out = []
    for (const m of fragment.matchAll(re)) out.push(m[1])
    return out
}

/** Text of the first <local>…</local> in a fragment; '' for a self-closing element; null when absent. */
function text(fragment, local) {
    const open = new RegExp(`<${NAME}${local}(?:\\s[^>]*)?(\\/?)>`)
    const m = fragment.match(open)
    if (!m) return null
    if (m[1] === '/') return ''
    const rest = fragment.slice(m.index + m[0].length)
    const close = rest.match(new RegExp(`<\\/${NAME}${local}\\s*>`))
    return unescapeXml((close ? rest.slice(0, close.index) : rest).trim())
}

function has(fragment, local) {
    return new RegExp(`<${NAME}${local}(?:\\s[^>]*)?\\/?>`).test(fragment)
}

function statusCode(fragment) {
    const s = text(fragment, 'status')
    const m = s && s.match(/HTTP\/\d(?:\.\d)?\s+(\d{3})/)
    return m ? Number(m[1]) : null
}

/**
 * Parse a multistatus body into one record per <response>.
 * @returns {Array<{href: string, status: number|null, props: object}>}
 *   props: displayname, getcontentlength, getlastmodified, getetag,
 *   getcontenttype (strings or null) and isCollection (boolean). The props
 *   come from the 2xx propstat; `status` is the member's own status: the
 *   response-level <status> when present, else the best propstat status.
 */
export function parseMultistatus(xml) {
    const records = []
    for (const response of blocks(xml, 'response')) {
        const href = text(response, 'href')
        if (href === null) continue
        const propstats = blocks(response, 'propstat')
        let outside = response
        for (const ps of propstats) outside = outside.replace(ps, '')
        const responseStatus = statusCode(outside)
        let status = responseStatus
        let propFragment = ''
        let best = null
        for (const ps of propstats) {
            const code = statusCode(ps)
            if (code !== null && code >= 200 && code < 300) {
                propFragment += (blocks(ps, 'prop')[0] ?? '')
                best = best ?? code
            } else if (best === null && code !== null) {
                best = best ?? code
            }
        }
        if (status === null) status = best
        const resourcetype = blocks(propFragment, 'resourcetype')[0] ?? ''
        records.push({
            href: unescapeXml(href),
            status,
            props: {
                displayname: text(propFragment, 'displayname'),
                getcontentlength: text(propFragment, 'getcontentlength'),
                getlastmodified: text(propFragment, 'getlastmodified'),
                getetag: text(propFragment, 'getetag'),
                getcontenttype: text(propFragment, 'getcontenttype'),
                isCollection: has(resourcetype, 'collection'),
            },
        })
    }
    return records
}
